export const MAP_BOUNDARY_STORAGE_KEY = 'iewrs.map-boundary-sources.v1'
export const MAP_BOUNDARY_EVENT = 'iewrs:map-boundary-sources-changed'

export const DEFAULT_MAP_BOUNDARIES = Object.freeze({
  province: '/geojson/rwanda-adm1.geojson',
  district: '/geojson/rwanda-adm2-nisr.geojson',
  sector: '/geojson/rwanda-adm3-nisr.geojson',
  hierarchy: '/geojson/rwanda-hierarchy.json',
})

// Perf action 11.1 — at country-wide zoom all 416 sector polygons are on
// screen and full NISR resolution (9.45 MB, 525k vertices) is parse/memory
// cost with no visible return; rwanda-adm3-simplified.geojson is the same
// 416 features (identical shapeID/shapeName/district/province — see
// scripts/build-simplified-sectors.mjs and its regression test) at ~10% of
// the vertices. Reuses SECTOR_LABEL_GRAIN_ZOOM rather than a new threshold:
// that is already this codebase's definition of "individual sectors start
// mattering", used to switch sector labels from district- to sector-grain.
export const SIMPLIFIED_SECTOR_BOUNDARY = '/geojson/rwanda-adm3-simplified.geojson'
export const SECTOR_ZOOM_SIMPLIFY_THRESHOLD = 8

export function sectorZoomBand(zoomLevel) {
  return zoomLevel < SECTOR_ZOOM_SIMPLIFY_THRESHOLD ? 'country' : 'drilldown'
}

// Only swaps when the sector source is still the shipped default. An admin
// who has pointed `sector` at a replacement boundary (settings UI) has no
// guarantee our pre-built simplified file was derived from THEIR polygons,
// so this always falls back to full resolution rather than risk serving
// geometry that doesn't match the configured source.
export function sectorBoundaryUrlForZoomBand(zoomBand, boundaryConfig = DEFAULT_MAP_BOUNDARIES) {
  const usingDefaultSector = boundaryConfig.sector === DEFAULT_MAP_BOUNDARIES.sector
  return usingDefaultSector && zoomBand === 'country' ? SIMPLIFIED_SECTOR_BOUNDARY : boundaryConfig.sector
}

export function validateBoundaryConfig(candidate) {
  const value = { ...DEFAULT_MAP_BOUNDARIES, ...(candidate || {}) }
  const errors = {}
  for (const [key, url] of Object.entries(value)) {
    if (!String(url || '').trim()) {
      errors[key] = 'A source URL is required.'
      continue
    }
    try {
      const parsed = new URL(url, window.location.origin)
      if (!['http:', 'https:'].includes(parsed.protocol)) errors[key] = 'Use an HTTP(S) URL or same-origin path.'
    } catch {
      errors[key] = 'Enter a valid URL or same-origin path.'
    }
  }
  return { value, errors, valid: Object.keys(errors).length === 0 }
}

const MIN_FEATURES = { province: 5, district: 30, sector: 416 }

export function validateBoundaryDocument(key, body) {
  if (key === 'hierarchy') {
    const hierarchy = body?.hierarchy
    const districts = body?.districtToProvince
    const sectors = body?.sectorToDistrict
    if (!hierarchy || typeof hierarchy !== 'object' || Array.isArray(hierarchy) || Object.keys(hierarchy).length < 5
        || !districts || typeof districts !== 'object' || Object.keys(districts).length < 30
        || !sectors || typeof sectors !== 'object' || Object.keys(sectors).length < 416) {
      return { valid: false, message: 'Hierarchy must cover 5 provinces, 30 districts, and 416 sectors.' }
    }
    const provinceNames = new Set(Object.keys(hierarchy))
    const districtNames = new Set(Object.keys(districts))
    if (Object.entries(districts).some(([, province]) => !provinceNames.has(province))) {
      return { valid: false, message: 'Every district must reference a province present in the hierarchy.' }
    }
    if (Object.entries(sectors).some(([, district]) => !districtNames.has(district))) {
      return { valid: false, message: 'Every sector must reference a district present in the hierarchy.' }
    }
    const hierarchyDistricts = new Set()
    const hierarchySectorIds = new Set()
    for (const districtMap of Object.values(hierarchy)) {
      if (!districtMap || typeof districtMap !== 'object' || Array.isArray(districtMap)) {
        return { valid: false, message: 'Each province must contain a district map.' }
      }
      for (const [district, sectorRows] of Object.entries(districtMap)) {
        hierarchyDistricts.add(district)
        if (!Array.isArray(sectorRows)) return { valid: false, message: 'Each hierarchy district must contain sectors.' }
        for (const sector of sectorRows) {
          if (!sector?.shapeID || !sector?.name) return { valid: false, message: 'Hierarchy sectors require shapeID and name.' }
          hierarchySectorIds.add(String(sector.shapeID))
        }
      }
    }
    for (const [province, districtMap] of Object.entries(hierarchy)) {
      for (const [district, sectorRows] of Object.entries(districtMap)) {
        if (districts[district] !== province) {
          return { valid: false, message: `District ${district} must map to its containing province.` }
        }
        for (const sector of sectorRows) {
          if (sectors[String(sector.shapeID)] !== district) {
            return { valid: false, message: `Sector ${sector.name} must map to its containing district.` }
          }
        }
      }
    }
    if ([...districtNames].some((district) => !hierarchyDistricts.has(district))
        || Object.keys(sectors).some((sectorId) => !hierarchySectorIds.has(String(sectorId)))) {
      return { valid: false, message: 'Hierarchy contents must agree with district and sector lookup maps.' }
    }
    return { valid: true }
  }

  if (body?.type !== 'FeatureCollection' || !Array.isArray(body.features)
      || body.features.length < MIN_FEATURES[key]) {
    return { valid: false, message: `Expected at least ${MIN_FEATURES[key]} ${key} features.` }
  }
  const validFeatures = body.features.every((feature) => {
    const geometry = feature?.geometry
    const properties = feature?.properties
    return feature?.type === 'Feature'
      && ['Polygon', 'MultiPolygon'].includes(geometry?.type)
      && Array.isArray(geometry?.coordinates) && geometry.coordinates.length > 0
      && properties?.shapeID != null
      && String(properties?.shapeName || '').trim().length > 0
  })
  return validFeatures
    ? { valid: true }
    : { valid: false, message: `${key} features require polygon geometry plus shapeID and shapeName.` }
}

export function validateBoundaryBundle(documents) {
  const hierarchy = documents?.hierarchy
  if (!hierarchy) return { valid: false, message: 'Hierarchy document is required.' }
  const provinceNames = new Set((documents.province?.features || []).map((f) => String(f.properties?.shapeName)))
  const districtNames = new Set((documents.district?.features || []).map((f) => String(f.properties?.shapeName)))
  const sectorLocations = new Set((documents.sector?.features || []).map((f) =>
    `${String(f.properties?.districtName || '')}__${String(f.properties?.shapeName || '')}`
  ))
  if (Object.keys(hierarchy.hierarchy || {}).some((name) => !provinceNames.has(name))) {
    return { valid: false, message: 'Province GeoJSON does not agree with the hierarchy.' }
  }
  if (Object.keys(hierarchy.districtToProvince || {}).some((name) => !districtNames.has(name))) {
    return { valid: false, message: 'District GeoJSON does not agree with the hierarchy.' }
  }
  const hierarchySectorLocations = []
  for (const districtMap of Object.values(hierarchy.hierarchy || {})) {
    for (const [district, sectors] of Object.entries(districtMap || {})) {
      for (const sector of sectors || []) hierarchySectorLocations.push(`${district}__${sector.name}`)
    }
  }
  const matchingSectors = hierarchySectorLocations.filter((location) => sectorLocations.has(location)).length
  // The shipped NISR source has two documented naming differences from the
  // legacy hierarchy (Shyrongi/Shyorongi and retired Nyakabuye). Require all
  // but those two; anything broader is a materially incompatible boundary set.
  if (matchingSectors < hierarchySectorLocations.length - 2) {
    return { valid: false, message: 'Sector GeoJSON does not sufficiently agree with the hierarchy.' }
  }
  return { valid: true }
}

export function readBoundaryConfig(storage = window.localStorage) {
  try {
    const raw = storage.getItem(MAP_BOUNDARY_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_MAP_BOUNDARIES }
    const parsed = JSON.parse(raw)
    const checked = validateBoundaryConfig(parsed)
    return checked.valid ? checked.value : { ...DEFAULT_MAP_BOUNDARIES }
  } catch {
    return { ...DEFAULT_MAP_BOUNDARIES }
  }
}

export function saveBoundaryConfig(candidate, storage = window.localStorage) {
  const checked = validateBoundaryConfig(candidate)
  if (!checked.valid) return checked
  storage.setItem(MAP_BOUNDARY_STORAGE_KEY, JSON.stringify(checked.value))
  window.dispatchEvent(new CustomEvent(MAP_BOUNDARY_EVENT, { detail: checked.value }))
  return checked
}

export function resetBoundaryConfig(storage = window.localStorage) {
  storage.removeItem(MAP_BOUNDARY_STORAGE_KEY)
  const value = { ...DEFAULT_MAP_BOUNDARIES }
  window.dispatchEvent(new CustomEvent(MAP_BOUNDARY_EVENT, { detail: value }))
  return value
}

export async function verifyAndSaveBoundaryConfig(candidate, {
  fetcher = window.fetch.bind(window),
  storage = window.localStorage,
} = {}) {
  const checked = validateBoundaryConfig(candidate)
  if (!checked.valid) return { ...checked, checks: {} }
  const documents = {}
  const entries = await Promise.all(Object.entries(checked.value).map(async ([key, url]) => {
    try {
      const response = await fetcher(url, { cache: 'no-store' })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const body = await response.json()
      const semantic = validateBoundaryDocument(key, body)
      if (!semantic.valid) throw new Error(semantic.message)
      documents[key] = body
      return [key, { ok: true }]
    } catch (error) {
      return [key, { ok: false, message: error.message || 'Unavailable' }]
    }
  }))
  const checks = Object.fromEntries(entries)
  if (entries.some(([, result]) => !result.ok)) {
    return { valid: false, value: checked.value, errors: {}, checks }
  }
  const bundle = validateBoundaryBundle(documents)
  if (!bundle.valid) {
    return {
      valid: false,
      value: checked.value,
      errors: {},
      checks: { ...checks, hierarchy: { ok: false, message: bundle.message } },
    }
  }
  storage.setItem(MAP_BOUNDARY_STORAGE_KEY, JSON.stringify(checked.value))
  window.dispatchEvent(new CustomEvent(MAP_BOUNDARY_EVENT, { detail: checked.value }))
  return { valid: true, value: checked.value, errors: {}, checks }
}

export function resetMapToCountry(map, bounds, options = {}) {
  if (!map?.fitBounds) return false
  map.resize?.()
  map.fitBounds(bounds, { padding: 20, duration: 300, ...options })
  return true
}

// Live prod bug — minZoom is set nowhere in this app. The only zoom bound
// left is `maxZoom: 11` on the interaction handlers; the narrow legacy
// MAX_BOUNDS used to supply a zoom-out floor as a side effect (maxBounds
// constrains zoom too, see MapLibreChoropleth's own comment on that), and
// widening it to the shared, loose PAN_MAX_BOUNDS removed that floor
// without anything replacing it — a user can zoom out to world view.
//
// Order is load-bearing: compute the floor from `cameraForBounds` (reads
// the bounds without moving the camera, same pattern zoomToFeature already
// uses), call `setMinZoom` with it, THEN fit. Reversed — fit first, floor
// after — a stale minZoom from a previous container size can clamp the fit
// that's about to run.
export function fitCountryWithMinZoom(map, bounds, options = {}) {
  if (!map?.fitBounds) return false
  const { padding = 20, duration = 300, ...rest } = options
  if (map.cameraForBounds && map.setMinZoom) {
    const camera = map.cameraForBounds(bounds, { padding })
    if (camera && typeof camera.zoom === 'number') map.setMinZoom(Math.max(1, camera.zoom - 0.75))
  }
  return resetMapToCountry(map, bounds, { padding, duration, ...rest })
}

// Visual Creator bug #6 — the "Full country" reset button was always
// enabled, including while the map was ALREADY showing the full-country
// fit, where clicking it does nothing visible (root cause: no state ever
// compared the live camera against the country-fit baseline). This is the
// pure comparison the button's disabled/enabled state is derived from —
// MapLibreChoropleth captures a `{zoom, lng, lat}` baseline right after its
// own initial fitBounds settles, tracks the live camera the same shape on
// every 'move' event, and disables the button when the two are within
// tolerance of each other (a real "already there" state, not a pixel-exact
// match, which floating-point easing would never hit).
export function isNearCountryFit(current, baseline, { zoomTolerance = 0.15, centerToleranceDeg = 0.05 } = {}) {
  if (!current || !baseline) return true // no baseline captured yet — nothing meaningful to reset TO, so treat as "already there" (disabled) rather than falsely enabling a no-op button
  // EPS absorbs IEEE-754 subtraction noise at/near the tolerance boundary
  // (e.g. `7.15 - 7` is `0.15000000000000036`, not exactly `0.15`) so a
  // value mathematically AT the tolerance edge doesn't spuriously read as
  // "past" it — found by an adversarial boundary test, not from live data
  // (real camera deltas are continuous floats that essentially never land
  // exactly on the tolerance edge), but cheap to close either way.
  const EPS = 1e-6
  const zoomDiff = Math.abs((current.zoom ?? 0) - (baseline.zoom ?? 0))
  const lngDiff = Math.abs((current.lng ?? 0) - (baseline.lng ?? 0))
  const latDiff = Math.abs((current.lat ?? 0) - (baseline.lat ?? 0))
  zoomTolerance += EPS
  centerToleranceDeg += EPS
  return zoomDiff <= zoomTolerance && lngDiff <= centerToleranceDeg && latDiff <= centerToleranceDeg
}
